import { request } from '../base';
import type { ApiResponse, PaginatedResponse, PaginationParams } from '../base';

// ================================
// AI Insights
// ================================

export async function analyzeAI() {
  return request<{ data: any }>('/ai/analyze', { method: 'POST' });
}

export async function getAIAnalysis() {
  return request<{ data: any }>('/ai/analyze');
}

export async function askAI(question: string) {
  return request<{ data: { answer: string; answeredAt: string } }>('/ai/ask', {
    method: 'POST',
    body: JSON.stringify({ question }),
  });
}

export async function getQuickInsights() {
  return request<{ data: any[] }>('/ai/quick-insights');
}

// Price Lists
export async function getPriceLists(status?: string) {
  const query = status ? `?status=${status}` : '';
  return request<{ data: any[] }>(`/price-lists${query}`);
}

export async function getPriceList(id: string) {
  return request<{ data: any }>(`/price-lists/${id}`);
}

export async function createPriceList(data: {
  name: string;
  code?: string;
  description?: string;
  currency?: string;
  isDefault?: boolean;
  validFrom?: string;
  validTo?: string;
  discountPercent?: number;
  marginPercent?: number;
  notes?: string;
}) {
  return request<{ data: any }>('/price-lists', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePriceList(id: string, data: any) {
  return request<{ data: any }>(`/price-lists/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deletePriceList(id: string) {
  return request<{ success: boolean }>(`/price-lists/${id}`, { method: 'DELETE' });
}

export async function addPriceListItem(priceListId: string, data: {
  formulaId: string;
  formulaCode: string;
  formulaName: string;
  basePrice: number;
  unit?: string;
  currency?: string;
  minQuantity?: number;
  discountPercent?: number;
  notes?: string;
}) {
  return request<{ data: any }>(`/price-lists/${priceListId}/items`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function removePriceListItem(priceListId: string, itemId: string) {
  return request<{ success: boolean }>(`/price-lists/${priceListId}/items/${itemId}`, {
    method: 'DELETE',
  });
}

export async function bulkAddPriceListItems(priceListId: string, items: any[]) {
  return request<{ data: any[]; count: number }>(`/price-lists/${priceListId}/items/bulk`, {
    method: 'POST',
    body: JSON.stringify({ items }),
  });
}

export async function assignCustomerToPriceList(priceListId: string, data: {
  customerId: string;
  customerName: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  notes?: string;
}) {
  return request<{ data: any }>(`/price-lists/${priceListId}/customers`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function removeCustomerFromPriceList(priceListId: string, customerId: string) {
  return request<{ success: boolean }>(`/price-lists/${priceListId}/customers/${customerId}`, {
    method: 'DELETE',
  });
}

export async function lookupPrice(formulaId: string, customerId?: string, quantity?: number) {
  const params = new URLSearchParams({ formulaId });
  if (customerId) params.set('customerId', customerId);
  if (quantity) params.set('quantity', quantity.toString());
  return request<{ data: any }>(`/price-lists/lookup?${params.toString()}`);
}

export async function getPriceListStats() {
  return request<{ data: any }>('/price-lists/stats');
}

// Report Schedules
export async function getReportTypes() {
  return request<{ data: any[] }>('/reports/types');
}

export async function getReportSchedules(params?: {
  reportType?: string;
  isActive?: boolean;
}) {
  const query = new URLSearchParams();
  if (params?.reportType) query.set('reportType', params.reportType);
  if (params?.isActive !== undefined) query.set('isActive', params.isActive.toString());
  const queryString = query.toString();
  return request<{ data: any[] }>(`/reports/schedules${queryString ? `?${queryString}` : ''}`);
}

export async function getReportSchedule(id: string) {
  return request<{ data: any }>(`/reports/schedules/${id}`);
}

export async function createReportSchedule(data: {
  name: string;
  reportType: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  dayOfWeek?: number;
  dayOfMonth?: number;
  timeOfDay?: string;
  recipients: string[];
  parameters?: Record<string, any>;
  format?: 'pdf' | 'excel' | 'csv';
}) {
  return request<{ data: any }>('/reports/schedules', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateReportSchedule(id: string, data: {
  name?: string;
  frequency?: 'daily' | 'weekly' | 'monthly';
  dayOfWeek?: number;
  dayOfMonth?: number;
  timeOfDay?: string;
  recipients?: string[];
  parameters?: Record<string, any>;
  format?: 'pdf' | 'excel' | 'csv';
  isActive?: boolean;
}) {
  return request<{ data: any }>(`/reports/schedules/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteReportSchedule(id: string) {
  return request<{ success: boolean }>(`/reports/schedules/${id}`, { method: 'DELETE' });
}

// Report Generation
export async function generateReport(data: {
  reportType: string;
  parameters?: Record<string, any>;
  format?: 'pdf' | 'excel' | 'csv';
  recipients?: string[];
}) {
  return request<{ data: any }>('/reports/generate', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getReportHistory(params?: {
  reportType?: string;
  status?: string;
  scheduleId?: string;
  limit?: number;
}) {
  const query = new URLSearchParams();
  if (params?.reportType) query.set('reportType', params.reportType);
  if (params?.status) query.set('status', params.status);
  if (params?.scheduleId) query.set('scheduleId', params.scheduleId);
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<{ data: any[] }>(`/reports/history${queryString ? `?${queryString}` : ''}`);
}

export async function getReportHistoryItem(id: string) {
  return request<{ data: any }>(`/reports/history/${id}`);
}

// KPIs and Analytics
export async function getKPIs(params?: {
  startDate?: string;
  endDate?: string;
}) {
  const query = new URLSearchParams();
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  const queryString = query.toString();
  return request<{ data: any }>(`/reports/kpis${queryString ? `?${queryString}` : ''}`);
}

export async function getKPITrends(params?: {
  period?: 'daily' | 'weekly' | 'monthly';
  periods?: number;
}) {
  const query = new URLSearchParams();
  if (params?.period) query.set('period', params.period);
  if (params?.periods) query.set('periods', params.periods.toString());
  const queryString = query.toString();
  return request<{ data: any }>(`/reports/kpis/trends${queryString ? `?${queryString}` : ''}`);
}

export async function exportReport(type: string, params?: {
  startDate?: string;
  endDate?: string;
  format?: 'csv' | 'json';
}) {
  const query = new URLSearchParams();
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.format) query.set('format', params.format);
  const queryString = query.toString();
  return request<any>(`/reports/export/${type}${queryString ? `?${queryString}` : ''}`);
}

// ================================
// Contracts
// ================================

export async function getContracts(params?: { status?: string; customerId?: string }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.customerId) query.set('customerId', params.customerId);
  const queryString = query.toString();
  return request<{ data: any[]; statuses: string[] }>(`/contracts${queryString ? `?${queryString}` : ''}`);
}

export async function getContract(id: string) {
  return request<{ data: any }>(`/contracts/${id}`);
}

export async function createContract(data: {
  customerId: string;
  customerName: string;
  title: string;
  description?: string;
  type?: string;
  startDate: string;
  endDate: string;
  currency?: string;
  paymentTerms?: string;
  deliveryTerms?: string;
  minimumOrderQty?: number;
  priceListId?: string;
  discountPercent?: number;
  notes?: string;
  termsAndConditions?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
}) {
  return request<{ data: any }>('/contracts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateContract(id: string, data: any) {
  return request<{ data: any }>(`/contracts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function updateContractStatus(id: string, status: string, reason?: string) {
  return request<{ data: any }>(`/contracts/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status, reason }),
  });
}

export async function deleteContract(id: string) {
  return request<{ success: boolean }>(`/contracts/${id}`, {
    method: 'DELETE',
  });
}

// Contract Items
export async function addContractItem(contractId: string, data: {
  formulaId: string;
  formulaCode: string;
  formulaName: string;
  quantity: number;
  unit?: string;
  unitPrice: number;
  currency?: string;
  discountPercent?: number;
  specifications?: string;
  notes?: string;
}) {
  return request<{ data: any }>(`/contracts/${contractId}/items`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateContractItem(contractId: string, itemId: string, data: any) {
  return request<{ data: any }>(`/contracts/${contractId}/items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function removeContractItem(contractId: string, itemId: string) {
  return request<{ success: boolean }>(`/contracts/${contractId}/items/${itemId}`, {
    method: 'DELETE',
  });
}

// Delivery Schedules
export async function addDeliverySchedule(contractId: string, data: {
  contractItemId?: string;
  formulaId?: string;
  formulaName?: string;
  scheduledDate: string;
  scheduledQty: number;
  unit?: string;
  deliveryNote?: string;
  notes?: string;
}) {
  return request<{ data: any }>(`/contracts/${contractId}/schedules`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDeliverySchedule(contractId: string, scheduleId: string, data: any) {
  return request<{ data: any }>(`/contracts/${contractId}/schedules/${scheduleId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteDeliverySchedule(contractId: string, scheduleId: string) {
  return request<{ success: boolean }>(`/contracts/${contractId}/schedules/${scheduleId}`, {
    method: 'DELETE',
  });
}

export async function generateDeliverySchedule(contractId: string, data: {
  frequency: 'weekly' | 'biweekly' | 'monthly' | 'quarterly';
  startDate?: string;
}) {
  return request<{ data: any[]; count: number }>(`/contracts/${contractId}/generate-schedule`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Contract Stats
export async function getContractStats() {
  return request<{ data: any }>('/contracts/stats/overview');
}

export async function getContractsByCustomer() {
  return request<{ data: any[] }>('/contracts/stats/by-customer');
}

// ================================
// Certifications
// ================================

export async function getCertificationTypes() {
  return request<{ data: any[]; grouped: Record<string, any[]> }>('/certifications/types');
}

export async function createCertificationType(data: {
  name: string;
  category?: string;
  description?: string;
}) {
  return request<{ data: any }>('/certifications/types', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getCertifications(params?: {
  entityType?: string;
  entityId?: string;
  type?: string;
  status?: string;
}) {
  const query = new URLSearchParams();
  if (params?.entityType) query.set('entityType', params.entityType);
  if (params?.entityId) query.set('entityId', params.entityId);
  if (params?.type) query.set('type', params.type);
  if (params?.status) query.set('status', params.status);
  const queryString = query.toString();
  return request<{ data: any[] }>(`/certifications${queryString ? `?${queryString}` : ''}`);
}

export async function getCertification(id: string) {
  return request<{ data: any }>(`/certifications/${id}`);
}

export async function createCertification(data: {
  entityType: string;
  entityId: string;
  entityName: string;
  certificationTypeId: string;
  certificationTypeName: string;
  certifyingBody?: string;
  certificateNumber?: string;
  issueDate?: string;
  expiryDate?: string;
  status?: string;
  scope?: string;
  restrictions?: string;
  notes?: string;
  reminderDays?: number;
}) {
  return request<{ data: any }>('/certifications', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCertification(id: string, data: any) {
  return request<{ data: any }>(`/certifications/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCertification(id: string) {
  return request<{ success: boolean }>(`/certifications/${id}`, {
    method: 'DELETE',
  });
}

export async function renewCertification(id: string, data: {
  issueDate?: string;
  expiryDate: string;
  certificateNumber?: string;
  notes?: string;
}) {
  return request<{ data: any }>(`/certifications/${id}/renew`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getEntityCertifications(entityType: string, entityId: string) {
  return request<{ data: any[] }>(`/certifications/entity/${entityType}/${entityId}`);
}

export async function checkCompliance(formulaId: string, requiredCertifications: string[]) {
  return request<{ data: any }>('/certifications/check-compliance', {
    method: 'POST',
    body: JSON.stringify({ formulaId, requiredCertifications }),
  });
}

export async function getCertificationDashboard() {
  return request<{ data: any }>('/certifications/stats/dashboard');
}

export async function getCertificationAlerts() {
  return request<{ data: any[]; count: number }>('/certifications/alerts');
}

// ================================
// Shipping & Logistics
// ================================

export async function getShipments(params?: {
  status?: string;
  customerId?: string;
  orderId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.orderId) query.set('orderId', params.orderId);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.search) query.set('search', params.search);
  if (params?.page) query.set('page', params.page.toString());
  if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
  const queryString = query.toString();
  return request<{
    data: any[];
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  }>(`/shipping${queryString ? `?${queryString}` : ''}`);
}

export async function getShipmentStatuses() {
  return request<{ data: { value: string; label: string; color: string }[] }>('/shipping/statuses');
}

export async function getCarriers() {
  return request<{ data: { value: string; label: string; trackingUrl: string }[] }>('/shipping/carriers');
}

export async function getShipment(id: string) {
  return request<{ data: any }>(`/shipping/${id}`);
}

export async function createShipment(data: {
  orderId: string;
  carrier?: string;
  trackingNumber?: string;
  shippingMethod?: string;
  shippingCost?: number;
  shippingCostCurrency?: string;
  weight?: number;
  weightUnit?: string;
  dimensions?: { length?: number; width?: number; height?: number; unit?: string };
  packageCount?: number;
  pickupAddress?: string;
  deliveryAddress?: string;
  estimatedDeliveryDate?: string;
  notes?: string;
  internalNotes?: string;
  lines?: {
    orderLineId?: string;
    batchId?: string;
    formulaId?: string;
    productName: string;
    quantity: number;
    unit?: string;
    lotNumber?: string;
    notes?: string;
  }[];
}) {
  return request<{ data: { id: string; shipmentNumber: string }; message: string }>('/shipping', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateShipment(id: string, data: {
  carrier?: string;
  trackingNumber?: string;
  trackingUrl?: string;
  shippingMethod?: string;
  shippingCost?: number;
  shippingCostCurrency?: string;
  weight?: number;
  weightUnit?: string;
  dimensions?: { length?: number; width?: number; height?: number; unit?: string };
  packageCount?: number;
  pickupAddress?: string;
  deliveryAddress?: string;
  estimatedDeliveryDate?: string;
  notes?: string;
  internalNotes?: string;
}) {
  return request<{ success: boolean; message: string }>(`/shipping/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function updateShipmentStatus(id: string, data: {
  status: string;
  location?: string;
  description?: string;
  signedBy?: string;
}) {
  return request<{ success: boolean; message: string }>(`/shipping/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function addShipmentEvent(id: string, data: {
  eventType?: string;
  status?: string;
  location?: string;
  description?: string;
  eventTime?: string;
}) {
  return request<{ data: { id: string }; message: string }>(`/shipping/${id}/events`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteShipment(id: string) {
  return request<{ success: boolean; message: string }>(`/shipping/${id}`, {
    method: 'DELETE',
  });
}

export async function getOrderShipments(orderId: string) {
  return request<{ data: any[] }>(`/shipping/order/${orderId}`);
}

export async function getShippingStats() {
  return request<{
    data: {
      byStatus: Record<string, number>;
      last30Days: number;
      avgDeliveryDays: number | null;
      pending: number;
      inTransit: number;
      delivered: number;
    };
  }>('/shipping/stats/overview');
}

// ================================
// Formula Comparison
// ================================

export async function compareFormulas(formulaIds: string[]) {
  return request<{ data: any }>(`/formula-compare/compare?ids=${formulaIds.join(',')}`);
}

export async function getFormulaVersionComparison(formulaId: string) {
  return request<{ data: any }>(`/formula-compare/${formulaId}/versions`);
}

export async function getSimilarFormulas(formulaId: string, limit?: number) {
  const query = limit ? `?limit=${limit}` : '';
  return request<{ data: any[] }>(`/formula-compare/${formulaId}/similar${query}`);
}

export async function getIngredientUsage(materialId: string) {
  return request<{ data: any }>(`/formula-compare/ingredient-usage/${materialId}`);
}

// ==========================================
// Phase 6: Regulatory Compliance & Advanced Quality
// ==========================================

// SDS Documents
export async function getSDSDocuments(params?: {
  status?: string;
  formulaId?: string;
  rawMaterialId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.formulaId) query.set('formulaId', params.formulaId);
  if (params?.rawMaterialId) query.set('rawMaterialId', params.rawMaterialId);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; statusCounts: Record<string, number> }>(`/sds${queryString ? `?${queryString}` : ''}`);
}

export async function getSDSDocument(id: string) {
  return request<{ data: any }>(`/sds/${id}`);
}

export async function createSDSDocument(data: {
  productName: string;
  productCode?: string;
  formulaId?: string;
  rawMaterialId?: string;
  language?: string;
  signalWord?: string;
  ghsClassification?: any[];
  hazardStatements?: any[];
  precautionaryStatements?: any[];
  pictograms?: string[];
  physicalState?: string;
  color?: string;
  odor?: string;
  flashPoint?: string;
  casNumber?: string;
  sections?: any;
}) {
  return request<{ data: any }>('/sds', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSDSDocument(id: string, data: any) {
  return request<{ data: any }>(`/sds/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function submitSDSForReview(id: string) {
  return request<{ data: any }>(`/sds/${id}/submit`, { method: 'POST' });
}

export async function approveSDSDocument(id: string) {
  return request<{ data: any }>(`/sds/${id}/approve`, { method: 'POST' });
}

export async function publishSDSDocument(id: string) {
  return request<{ data: any }>(`/sds/${id}/publish`, { method: 'POST' });
}

export async function createSDSNewVersion(id: string) {
  return request<{ data: any }>(`/sds/${id}/new-version`, { method: 'POST' });
}

export async function generateSDSFromFormula(formulaId: string, data?: { language?: string; supplierDetails?: any; emergencyPhone?: string }) {
  return request<{ data: any }>(`/sds/generate/formula/${formulaId}`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

export async function deleteSDSDocument(id: string) {
  return request<{ success: boolean }>(`/sds/${id}`, { method: 'DELETE' });
}

export async function getSDSStats() {
  return request<{ data: any }>('/sds/stats/overview');
}

// Regulatory Substances
export async function getRegulatorySubstances(params?: {
  search?: string;
  reachStatus?: string;
  svhc?: boolean;
  source?: string;
  limit?: number;
  offset?: number;
}) {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.reachStatus) query.set('reachStatus', params.reachStatus);
  if (params?.svhc !== undefined) query.set('svhc', params.svhc.toString());
  if (params?.source) query.set('source', params.source);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/regulatory/substances${queryString ? `?${queryString}` : ''}`);
}

export async function getRegulatorySubstance(id: string) {
  return request<{ data: any }>(`/regulatory/substances/${id}`);
}

export async function lookupSubstanceByCAS(casNumber: string) {
  return request<{ data: any }>(`/regulatory/substances/lookup/${casNumber}`);
}

export async function createRegulatorySubstance(data: {
  casNumber?: string;
  commonName: string;
  ecNumber?: string;
  reachStatus?: string;
  svhcListed?: boolean;
  ifraCategory?: string;
  ifraMaxLevel?: number;
  source: string;
  notes?: string;
}) {
  return request<{ data: any }>('/regulatory/substances', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRegulatorySubstance(id: string, data: any) {
  return request<{ data: any }>(`/regulatory/substances/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteRegulatorySubstance(id: string) {
  return request<{ success: boolean }>(`/regulatory/substances/${id}`, { method: 'DELETE' });
}

export async function addSubstanceRestriction(substanceId: string, data: {
  region: string;
  regulation: string;
  restrictionType: string;
  maxConcentration?: number;
  effectiveDate?: string;
  notes?: string;
}) {
  return request<{ data: any }>(`/regulatory/substances/${substanceId}/restrictions`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function scanFormulaCompliance(formulaId: string, params?: { regions?: string[]; regulations?: string[] }) {
  return request<{ data: any }>(`/regulatory/scan/formula/${formulaId}`, {
    method: 'POST',
    body: JSON.stringify(params || {}),
  });
}

export async function getComplianceScans(params?: { entityType?: string; entityId?: string; status?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.entityType) query.set('entityType', params.entityType);
  if (params?.entityId) query.set('entityId', params.entityId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<{ data: any[] }>(`/regulatory/scans${queryString ? `?${queryString}` : ''}`);
}

export async function getComplianceScan(id: string) {
  return request<{ data: any }>(`/regulatory/scans/${id}`);
}

export async function getRegulatoryDashboard() {
  return request<{ data: any }>('/regulatory/dashboard');
}

export async function importRegulatorySubstances(substances: any[]) {
  return request<{ data: any }>('/regulatory/substances/import', {
    method: 'POST',
    body: JSON.stringify({ substances }),
  });
}

// Material Hazards & Enrichment
export async function getMaterialHazards(id: string) {
  return request<{ data: any }>(`/regulatory/materials/${id}/hazards`);
}

export async function getSvhcMaterials() {
  return request<{ data: any[]; count: number }>('/regulatory/materials/svhc');
}

export async function getEnrichmentStatus() {
  return request<{ data: { materials: any; enrichmentLog: any[] } }>('/regulatory/enrichment-status');
}

// IFRA Classes & Restrictions (DB-backed)
export async function getIfraClasses() {
  return request<{ data: any[] }>('/regulatory/ifra-classes');
}

export async function getIfraRestrictions(params?: { search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; pagination: any }>(`/regulatory/ifra-restrictions${queryString ? `?${queryString}` : ''}`);
}

export async function checkIfraCompliance(formulaId: string, usageAreaId?: string) {
  const query = new URLSearchParams({ formulaId });
  if (usageAreaId) query.set('usageAreaId', usageAreaId);
  return request<{ data: any }>(`/regulatory/ifra-restrictions/check?${query.toString()}`);
}

export async function getUsageAreas(params?: { search?: string; catalog?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.catalog) query.set('catalog', params.catalog);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; pagination: any }>(`/regulatory/usage-areas${queryString ? `?${queryString}` : ''}`);
}

export async function getFragranceNotes(params?: { position?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.position) query.set('position', params.position);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; pagination: any }>(`/regulatory/fragrance-notes${queryString ? `?${queryString}` : ''}`);
}

// Stability Studies
export async function getStabilityStudies(params?: { status?: string; studyType?: string; formulaId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.studyType) query.set('studyType', params.studyType);
  if (params?.formulaId) query.set('formulaId', params.formulaId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[] }>(`/stability${queryString ? `?${queryString}` : ''}`);
}

export async function getStabilityStudy(id: string) {
  return request<{ data: any }>(`/stability/${id}`);
}

export async function createStabilityStudy(data: {
  formulaId?: string;
  batchId?: string;
  lotId?: string;
  studyType: string;
  temperature?: string;
  humidity?: string;
  plannedDuration?: number;
  testingIntervals?: number[];
  startDate: string;
  initialShelfLife?: number;
  notes?: string;
}) {
  return request<{ data: any }>('/stability', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateStabilityStudy(id: string, data: any) {
  return request<{ data: any }>(`/stability/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function startStabilityStudy(id: string) {
  return request<{ data: any }>(`/stability/${id}/start`, { method: 'POST' });
}

export async function completeStabilityStudy(id: string, data: { confirmedShelfLife?: number; storageRecommendation?: string; conclusion?: string }) {
  return request<{ data: any }>(`/stability/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function approveStabilityStudy(id: string) {
  return request<{ data: any }>(`/stability/${id}/approve`, { method: 'POST' });
}

export async function cancelStabilityStudy(id: string, reason?: string) {
  return request<{ data: any }>(`/stability/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function recordStabilityTimepoint(studyId: string, timepointId: string, data: {
  actualDate?: string;
  testResults?: any;
  passesSpecification?: boolean;
  deviations?: string;
  notes?: string;
}) {
  return request<{ data: any }>(`/stability/${studyId}/timepoints/${timepointId}/results`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getUpcomingTimepoints(days?: number) {
  const query = days ? `?days=${days}` : '';
  return request<{ data: any[] }>(`/stability/timepoints/upcoming${query}`);
}

export async function getOverdueTimepoints() {
  return request<{ data: any[]; count: number }>('/stability/timepoints/overdue');
}

export async function getStabilityStats() {
  return request<{ data: any }>('/stability/stats/overview');
}

export async function deleteStabilityStudy(id: string) {
  return request<{ success: boolean }>(`/stability/${id}`, { method: 'DELETE' });
}

// SPC Control Charts
export async function getControlCharts(params?: { formulaId?: string; testDefinitionId?: string; isActive?: boolean; chartType?: string }) {
  const query = new URLSearchParams();
  if (params?.formulaId) query.set('formulaId', params.formulaId);
  if (params?.testDefinitionId) query.set('testDefinitionId', params.testDefinitionId);
  if (params?.isActive !== undefined) query.set('isActive', params.isActive.toString());
  if (params?.chartType) query.set('chartType', params.chartType);
  const queryString = query.toString();
  return request<{ data: any[] }>(`/spc/charts${queryString ? `?${queryString}` : ''}`);
}

export async function getControlChart(id: string, params?: { limit?: number; startDate?: string; endDate?: string }) {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  const queryString = query.toString();
  return request<{ data: any }>(`/spc/charts/${id}${queryString ? `?${queryString}` : ''}`);
}

export async function createControlChart(data: {
  name: string;
  testDefinitionId?: string;
  formulaId?: string;
  chartType?: string;
  upperSpecLimit?: number;
  lowerSpecLimit?: number;
  targetValue?: number;
  subgroupSize?: number;
}) {
  return request<{ data: any }>('/spc/charts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateControlChart(id: string, data: any) {
  return request<{ data: any }>(`/spc/charts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function recalculateControlChart(id: string) {
  return request<{ data: any; newLimits: any; capability: any }>(`/spc/charts/${id}/recalculate`, { method: 'POST' });
}

export async function deleteControlChart(id: string) {
  return request<{ success: boolean }>(`/spc/charts/${id}`, { method: 'DELETE' });
}

export async function addControlChartDataPoint(chartId: string, data: {
  value: number;
  batchId?: string;
  lotId?: string;
  testResultId?: string;
  measuredAt?: string;
}) {
  return request<{ data: any; alert: any | null }>(`/spc/charts/${chartId}/data`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function bulkAddControlChartData(chartId: string, dataPoints: any[]) {
  return request<{ data: any[]; count: number }>(`/spc/charts/${chartId}/data/bulk`, {
    method: 'POST',
    body: JSON.stringify({ dataPoints }),
  });
}

export async function getControlChartCapability(id: string) {
  return request<{ data: any }>(`/spc/charts/${id}/capability`);
}

export async function getSPCDashboard() {
  return request<{ data: any }>('/spc/dashboard');
}

// Electronic Signatures
export async function createElectronicSignature(data: {
  entityType: string;
  entityId: string;
  action: string;
  meaning?: string;
  password: string;
  reason?: string;
  verificationMethod?: string;
}) {
  return request<{ data: any }>('/esignatures', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getEntitySignatures(entityType: string, entityId: string) {
  return request<{ data: any[] }>(`/esignatures/entity/${entityType}/${entityId}`);
}

export async function verifySignature(id: string) {
  return request<{ data: any }>(`/esignatures/${id}/verify`);
}

export async function getUserSignatures(userId: string, params?: { limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[] }>(`/esignatures/user/${userId}${queryString ? `?${queryString}` : ''}`);
}

export async function getRecentSignatures(limit?: number) {
  const query = limit ? `?limit=${limit}` : '';
  return request<{ data: any[] }>(`/esignatures/recent${query}`);
}

export async function getSignatureStats() {
  return request<{ data: any }>('/esignatures/stats');
}

export async function verifySignatureChain(entityType: string, entityId: string, requiredActions: string[]) {
  return request<{ data: any }>('/esignatures/verify-chain', {
    method: 'POST',
    body: JSON.stringify({ entityType, entityId, requiredActions }),
  });
}

// ================================
// Invoices
// ================================

export async function getInvoices(params?: {
  status?: string;
  type?: string;
  customerId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.type) query.set('type', params.type);
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.search) query.set('search', params.search);
  if (params?.page) query.set('page', params.page.toString());
  if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
  const queryString = query.toString();
  return request<PaginatedResponse<any> & { summary?: any }>(`/invoices${queryString ? `?${queryString}` : ''}`);
}

export async function getInvoice(id: string) {
  return request<{ data: any }>(`/invoices/${id}`);
}

export async function getInvoiceStats(type?: string) {
  const query = type ? `?type=${type}` : '';
  return request<{ data: any }>(`/invoices/stats/overview${query}`);
}

export async function createInvoice(data: any) {
  return request<{ data: any }>('/invoices', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateInvoice(id: string, data: any) {
  return request<{ success: boolean }>(`/invoices/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function sendInvoice(id: string) {
  return request<{ success: boolean }>(`/invoices/${id}/send`, {
    method: 'POST',
  });
}

export async function markInvoicePaid(id: string, data?: { paymentDate?: string; paymentMethod?: string }) {
  return request<{ success: boolean }>(`/invoices/${id}/paid`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

export async function cancelInvoice(id: string, reason?: string) {
  return request<{ success: boolean }>(`/invoices/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function addInvoicePayment(id: string, data: { amount: number | string; paymentDate: string; paymentMethod?: string; referenceNumber?: string; reference?: string; notes?: string }) {
  return request<{ data: any }>(`/invoices/${id}/payments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getInvoicePayments(id: string) {
  return request<{ data: any[] }>(`/invoices/${id}/payments`);
}

// ================================
// Sustainability
// ================================

export async function getSustainabilityDashboard() {
  return request<{ data: any }>('/sustainability/dashboard');
}

export async function getSupplierSustainability(params?: { page?: number; pageSize?: number }) {
  const query = new URLSearchParams();
  if (params?.page) query.set('page', params.page.toString());
  if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
  const queryString = query.toString();
  return request<PaginatedResponse<any>>(`/sustainability/suppliers${queryString ? `?${queryString}` : ''}`);
}

export async function getCarbonFootprints(params?: { entityType?: string; startDate?: string; endDate?: string }) {
  const query = new URLSearchParams();
  if (params?.entityType) query.set('entityType', params.entityType);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  const queryString = query.toString();
  return request<{ data: any[] }>(`/sustainability/carbon${queryString ? `?${queryString}` : ''}`);
}

export async function createSupplierSustainabilityAssessment(data: any) {
  return request<{ data: any }>('/sustainability/suppliers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createCarbonFootprint(data: any) {
  return request<{ data: any }>('/sustainability/carbon', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ================================
// Inventory Transfers
// ================================

export async function getInventoryTransfers(params?: {
  status?: string;
  fromWarehouseId?: string;
  toWarehouseId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.fromWarehouseId) query.set('fromWarehouseId', params.fromWarehouseId);
  if (params?.toWarehouseId) query.set('toWarehouseId', params.toWarehouseId);
  if (params?.search) query.set('search', params.search);
  if (params?.page) query.set('page', params.page.toString());
  if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
  const queryString = query.toString();
  return request<PaginatedResponse<any>>(`/transfers${queryString ? `?${queryString}` : ''}`);
}

export async function getInventoryTransfer(id: string) {
  return request<{ data: any }>(`/transfers/${id}`);
}

export async function getTransferStats() {
  return request<{ data: any }>('/transfers/stats/overview');
}

export async function createInventoryTransfer(data: any) {
  return request<{ data: any }>('/transfers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function submitTransfer(id: string) {
  return request<{ success: boolean }>(`/transfers/${id}/submit`, {
    method: 'POST',
  });
}

export async function approveTransfer(id: string) {
  return request<{ success: boolean }>(`/transfers/${id}/approve`, {
    method: 'POST',
  });
}

export async function shipTransfer(id: string) {
  return request<{ success: boolean }>(`/transfers/${id}/ship`, {
    method: 'POST',
  });
}

export async function receiveTransfer(id: string, data?: { receivedItems?: any[] }) {
  return request<{ success: boolean }>(`/transfers/${id}/receive`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

export async function cancelTransfer(id: string, reason?: string) {
  return request<{ success: boolean }>(`/transfers/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// ================================
// Demand Forecasting
// ================================

export async function getDemandForecasts(params?: {
  status?: string;
  formulaId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.formulaId) query.set('formulaId', params.formulaId);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.page) query.set('page', params.page.toString());
  if (params?.pageSize) query.set('pageSize', params.pageSize.toString());
  const queryString = query.toString();
  return request<PaginatedResponse<any>>(`/forecasting${queryString ? `?${queryString}` : ''}`);
}

export async function getForecastAccuracyStats() {
  return request<{ data: any }>('/forecasting/stats/accuracy');
}

export async function getForecastSummary(months?: number) {
  const query = months ? `?months=${months}` : '';
  return request<{ data: any }>(`/forecasting/stats/summary${query}`);
}

export async function generateDemandForecast(data: {
  formulaId?: string;
  customerId?: string;
  periodStart?: string;
  periodEnd?: string;
  periods?: number;
  method?: string;
  historyMonths?: number;
  parameters?: any;
}) {
  return request<{ data: any }>('/forecasting', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function approveForecast(id: string) {
  return request<{ success: boolean }>(`/forecasting/${id}/approve`, {
    method: 'POST',
  });
}

export async function deleteForecast(id: string) {
  return request<{ success: boolean }>(`/forecasting/${id}`, {
    method: 'DELETE',
  });
}

export async function recordForecastActual(id: string, actualValue: number | { actualValue: number; notes?: string }) {
  const data = typeof actualValue === 'number' ? { actualValue } : actualValue;
  return request<{ success: boolean }>(`/forecasting/${id}/actual`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ================================
// Supply Chain Risks
// ================================

export async function getSupplyChainRisks(params?: { riskType?: string; severity?: string; status?: string; supplierId?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.riskType) query.set('riskType', params.riskType);
  if (params?.severity) query.set('severity', params.severity);
  if (params?.status) query.set('status', params.status);
  if (params?.supplierId) query.set('supplierId', params.supplierId);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<{ data: any[] }>(`/supply-chain-risks${queryString ? `?${queryString}` : ''}`);
}

export async function getSupplyChainRisk(id: string) {
  return request<{ data: any }>(`/supply-chain-risks/${id}`);
}

export async function createSupplyChainRisk(data: {
  riskType: string;
  severity: string;
  probability: string;
  title: string;
  description?: string;
  supplierId?: string;
  rawMaterialId?: string;
  region?: string;
  potentialImpact?: string;
  estimatedImpactCost?: string;
  mitigationStrategy?: string;
  assignedTo?: string;
}) {
  return request<{ data: any }>('/supply-chain-risks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRiskMitigation(id: string, data: { status: string; strategy?: string }) {
  return request<{ data: any }>(`/supply-chain-risks/${id}/mitigation`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function reviewRisk(id: string) {
  return request<{ data: any }>(`/supply-chain-risks/${id}/review`, {
    method: 'POST',
  });
}

export async function resolveRisk(id: string, resolution?: string) {
  return request<{ data: any }>(`/supply-chain-risks/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ resolution }),
  });
}

export async function deleteSupplyChainRisk(id: string) {
  return request<{ success: boolean }>(`/supply-chain-risks/${id}`, { method: 'DELETE' });
}

export async function getSupplyChainRiskDashboard() {
  return request<{ data: any }>('/supply-chain-risks/dashboard/overview');
}

export async function getSupplyChainRiskHeatmap() {
  return request<{ data: any }>('/supply-chain-risks/heatmap');
}

export async function resolveSupplyChainRisk(id: string, data?: { resolution?: string; notes?: string }) {
  return request<{ success: boolean }>(`/supply-chain-risks/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

// =====================================================
// PHASE 35: ADVANCED ANALYTICS & BUSINESS INTELLIGENCE
// =====================================================

// BI Dashboards
export async function getBiDashboards(params?: { type?: string; isPublic?: boolean; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.type) searchParams.set('type', params.type);
  if (params?.isPublic !== undefined) searchParams.set('isPublic', String(params.isPublic));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/bi/dashboards${query ? `?${query}` : ''}`);
}

export async function getBiDashboard(id: string) {
  return request<any>(`/bi/dashboards/${id}`);
}

export async function createBiDashboard(data: {
  name: string;
  description?: string;
  dashboardType: string;
  isDefault?: boolean;
  isPublic?: boolean;
  layout?: any;
  theme?: string;
  refreshIntervalSeconds?: number;
  filters?: any;
  dateRangePreset?: string;
  customDateStart?: string;
  customDateEnd?: string;
}) {
  return request<any>('/bi/dashboards', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBiDashboard(id: string, data: Partial<{
  name: string;
  description: string;
  dashboardType: string;
  isDefault: boolean;
  isPublic: boolean;
  layout: any;
  theme: string;
  refreshIntervalSeconds: number;
  filters: any;
  dateRangePreset: string;
  customDateStart: string;
  customDateEnd: string;
}>) {
  return request<any>(`/bi/dashboards/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteBiDashboard(id: string) {
  return request<{ success: boolean }>(`/bi/dashboards/${id}`, { method: 'DELETE' });
}

// BI Dashboard Widgets
export async function getBiDashboardWidgets(dashboardId: string) {
  return request<any[]>(`/bi/dashboards/${dashboardId}/widgets`);
}

export async function createBiWidget(dashboardId: string, data: {
  widgetType: string;
  title: string;
  subtitle?: string;
  description?: string;
  dataSource: string;
  metricId?: string;
  queryConfig: any;
  visualizationConfig?: any;
  positionX?: number;
  positionY?: number;
  width?: number;
  height?: number;
  refreshIntervalSeconds?: number;
  drillDownConfig?: any;
  conditionalFormatting?: any;
  isVisible?: boolean;
  sequenceOrder?: number;
}) {
  return request<any>(`/bi/dashboards/${dashboardId}/widgets`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBiWidget(id: string, data: Partial<{
  widgetType: string;
  title: string;
  subtitle: string;
  description: string;
  dataSource: string;
  metricId: string;
  queryConfig: any;
  visualizationConfig: any;
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  refreshIntervalSeconds: number;
  drillDownConfig: any;
  conditionalFormatting: any;
  isVisible: boolean;
  sequenceOrder: number;
}>) {
  return request<any>(`/bi/widgets/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteBiWidget(id: string) {
  return request<{ success: boolean }>(`/bi/widgets/${id}`, { method: 'DELETE' });
}

// BI Metrics (KPIs)
export async function getBiMetrics(params?: { category?: string; isActive?: boolean; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set('category', params.category);
  if (params?.isActive !== undefined) searchParams.set('isActive', String(params.isActive));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/bi/metrics${query ? `?${query}` : ''}`);
}

export async function getBiMetric(id: string) {
  return request<any>(`/bi/metrics/${id}`);
}

export async function createBiMetric(data: {
  code: string;
  name: string;
  description?: string;
  category: string;
  subcategory?: string;
  metricType: string;
  dataSource: string;
  calculationFormula?: string;
  unit?: string;
  decimalPlaces?: number;
  aggregationPeriod?: string;
  isCumulative?: boolean;
  direction?: string;
  targetValue?: number;
  warningThreshold?: number;
  criticalThreshold?: number;
  benchmarkValue?: number;
  benchmarkSource?: string;
  isActive?: boolean;
  isSystem?: boolean;
  tags?: string[];
  metadata?: any;
}) {
  return request<any>('/bi/metrics', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBiMetric(id: string, data: Partial<{
  code: string;
  name: string;
  description: string;
  category: string;
  subcategory: string;
  metricType: string;
  dataSource: string;
  calculationFormula: string;
  unit: string;
  decimalPlaces: number;
  aggregationPeriod: string;
  isCumulative: boolean;
  direction: string;
  targetValue: number;
  warningThreshold: number;
  criticalThreshold: number;
  benchmarkValue: number;
  benchmarkSource: string;
  isActive: boolean;
  tags: string[];
  metadata: any;
}>) {
  return request<any>(`/bi/metrics/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteBiMetric(id: string) {
  return request<{ success: boolean }>(`/bi/metrics/${id}`, { method: 'DELETE' });
}

// BI Metric Values
export async function getBiMetricValues(metricId: string, params?: { periodType?: string; startDate?: string; endDate?: string; limit?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.periodType) searchParams.set('periodType', params.periodType);
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  const query = searchParams.toString();
  return request<any[]>(`/bi/metrics/${metricId}/values${query ? `?${query}` : ''}`);
}

export async function createBiMetricValue(metricId: string, data: {
  periodStart: string;
  periodEnd: string;
  periodType: string;
  value: number;
  previousValue?: number;
  changeValue?: number;
  changePercentage?: number;
  targetValue?: number;
  varianceFromTarget?: number;
  status?: string;
  dimension1?: string;
  dimension2?: string;
  dimension3?: string;
  dimensionValues?: any;
  notes?: string;
}) {
  return request<any>(`/bi/metrics/${metricId}/values`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// BI Saved Reports
export async function getBiReports(params?: { category?: string; reportType?: string; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set('category', params.category);
  if (params?.reportType) searchParams.set('reportType', params.reportType);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/bi/reports${query ? `?${query}` : ''}`);
}

export async function getBiReport(id: string) {
  return request<any>(`/bi/reports/${id}`);
}

export async function createBiReport(data: {
  name: string;
  description?: string;
  reportType: string;
  category?: string;
  templateId?: string;
  queryConfig: any;
  columnsConfig?: any;
  filters?: any;
  grouping?: any;
  sorting?: any;
  totalsConfig?: any;
  chartConfig?: any;
  exportFormats?: string[];
  isPublic?: boolean;
  isFavorite?: boolean;
}) {
  return request<any>('/bi/reports', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBiReport(id: string, data: Partial<{
  name: string;
  description: string;
  reportType: string;
  category: string;
  templateId: string;
  queryConfig: any;
  columnsConfig: any;
  filters: any;
  grouping: any;
  sorting: any;
  totalsConfig: any;
  chartConfig: any;
  exportFormats: string[];
  isPublic: boolean;
  isFavorite: boolean;
}>) {
  return request<any>(`/bi/reports/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteBiReport(id: string) {
  return request<{ success: boolean }>(`/bi/reports/${id}`, { method: 'DELETE' });
}

export async function runBiReport(id: string, data?: { filters?: any; exportFormat?: string }) {
  return request<{ executionId: string; status: string }>(`/bi/reports/${id}/run`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

// BI Scheduled Reports
export async function getBiScheduledReports(params?: { isActive?: boolean; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.isActive !== undefined) searchParams.set('isActive', String(params.isActive));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/bi/scheduled-reports${query ? `?${query}` : ''}`);
}

export async function getBiScheduledReport(id: string) {
  return request<any>(`/bi/scheduled-reports/${id}`);
}

export async function createBiScheduledReport(data: {
  reportId: string;
  name: string;
  description?: string;
  scheduleType: string;
  scheduleConfig: any;
  timezone?: string;
  isActive?: boolean;
  exportFormat?: string;
  recipients: string[];
  ccRecipients?: string[];
  emailSubject?: string;
  emailBody?: string;
  includeEmpty?: boolean;
  nextRunAt?: string;
}) {
  return request<any>('/bi/scheduled-reports', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBiScheduledReport(id: string, data: Partial<{
  reportId: string;
  name: string;
  description: string;
  scheduleType: string;
  scheduleConfig: any;
  timezone: string;
  isActive: boolean;
  exportFormat: string;
  recipients: string[];
  ccRecipients: string[];
  emailSubject: string;
  emailBody: string;
  includeEmpty: boolean;
  nextRunAt: string;
}>) {
  return request<any>(`/bi/scheduled-reports/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteBiScheduledReport(id: string) {
  return request<{ success: boolean }>(`/bi/scheduled-reports/${id}`, { method: 'DELETE' });
}

// BI Report Executions
export async function getBiReportExecutions(params?: { reportId?: string; status?: string; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.reportId) searchParams.set('reportId', params.reportId);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/bi/report-executions${query ? `?${query}` : ''}`);
}

export async function getBiReportExecution(id: string) {
  return request<any>(`/bi/report-executions/${id}`);
}

// BI Alerts
export async function getBiAlerts(params?: { isActive?: boolean; severity?: string; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.isActive !== undefined) searchParams.set('isActive', String(params.isActive));
  if (params?.severity) searchParams.set('severity', params.severity);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/bi/alerts${query ? `?${query}` : ''}`);
}

export async function getBiAlert(id: string) {
  return request<any>(`/bi/alerts/${id}`);
}

export async function createBiAlert(data: {
  name: string;
  description?: string;
  alertType: string;
  metricId?: string;
  dataSource?: string;
  conditionConfig: any;
  thresholdValue?: number;
  comparisonOperator?: string;
  thresholdValue2?: number;
  trendPeriodDays?: number;
  trendDirection?: string;
  trendThresholdPercentage?: number;
  checkFrequencyMinutes?: number;
  severity?: string;
  isActive?: boolean;
  notificationChannels?: string[];
  recipients?: string[];
  webhookUrl?: string;
  slackChannel?: string;
  cooldownMinutes?: number;
}) {
  return request<any>('/bi/alerts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBiAlert(id: string, data: Partial<{
  name: string;
  description: string;
  alertType: string;
  metricId: string;
  dataSource: string;
  conditionConfig: any;
  thresholdValue: number;
  comparisonOperator: string;
  thresholdValue2: number;
  trendPeriodDays: number;
  trendDirection: string;
  trendThresholdPercentage: number;
  checkFrequencyMinutes: number;
  severity: string;
  isActive: boolean;
  notificationChannels: string[];
  recipients: string[];
  webhookUrl: string;
  slackChannel: string;
  cooldownMinutes: number;
}>) {
  return request<any>(`/bi/alerts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteBiAlert(id: string) {
  return request<{ success: boolean }>(`/bi/alerts/${id}`, { method: 'DELETE' });
}

// BI Alert History
export async function getBiAlertHistory(alertId: string, params?: { limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/bi/alerts/${alertId}/history${query ? `?${query}` : ''}`);
}

export async function acknowledgeBiAlert(historyId: string) {
  return request<any>(`/bi/alert-history/${historyId}/acknowledge`, { method: 'POST' });
}

export async function resolveBiAlert(historyId: string, data?: { resolutionNotes?: string }) {
  return request<any>(`/bi/alert-history/${historyId}/resolve`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

// BI Custom Queries
export async function getBiCustomQueries(params?: { limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/bi/custom-queries${query ? `?${query}` : ''}`);
}

export async function getBiCustomQuery(id: string) {
  return request<any>(`/bi/custom-queries/${id}`);
}

export async function createBiCustomQuery(data: {
  name: string;
  description?: string;
  queryType: string;
  baseTables: string[];
  selectColumns: any;
  joinConfig?: any;
  whereConditions?: any;
  groupByColumns?: string[];
  havingConditions?: any;
  orderByConfig?: any;
  limitRows?: number;
  parameters?: any;
  isPublic?: boolean;
  tags?: string[];
}) {
  return request<any>('/bi/custom-queries', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBiCustomQuery(id: string, data: Partial<{
  name: string;
  description: string;
  queryType: string;
  baseTables: string[];
  selectColumns: any;
  joinConfig: any;
  whereConditions: any;
  groupByColumns: string[];
  havingConditions: any;
  orderByConfig: any;
  limitRows: number;
  parameters: any;
  isPublic: boolean;
  tags: string[];
}>) {
  return request<any>(`/bi/custom-queries/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteBiCustomQuery(id: string) {
  return request<{ success: boolean }>(`/bi/custom-queries/${id}`, { method: 'DELETE' });
}

// BI Data Snapshots
export async function getBiSnapshots(params?: { category?: string; snapshotType?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set('category', params.category);
  if (params?.snapshotType) searchParams.set('snapshotType', params.snapshotType);
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/bi/snapshots${query ? `?${query}` : ''}`);
}

export async function getBiSnapshot(id: string) {
  return request<any>(`/bi/snapshots/${id}`);
}

export async function createBiSnapshot(data: {
  snapshotType: string;
  snapshotDate: string;
  category: string;
  subcategory?: string;
  data: any;
  summary?: any;
  rowCount?: number;
}) {
  return request<any>('/bi/snapshots', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// BI User Preferences
export async function getBiUserPreferences() {
  return request<any>('/bi/preferences');
}

export async function updateBiUserPreferences(data: Partial<{
  defaultDashboardId: string;
  favoriteDashboards: string[];
  favoriteReports: string[];
  favoriteMetrics: string[];
  recentDashboards: any;
  recentReports: any;
  defaultDateRange: string;
  defaultTimezone: string;
  chartPreferences: any;
  tablePreferences: any;
  notificationPreferences: any;
}>) {
  return request<any>('/bi/preferences', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// BI Dashboard Sharing
export async function getBiDashboardShares(dashboardId: string) {
  return request<any[]>(`/bi/dashboards/${dashboardId}/shares`);
}

export async function shareBiDashboard(dashboardId: string, data: {
  sharedWithUserId?: string;
  sharedWithRole?: string;
  permissionLevel?: string;
  expiresAt?: string;
}) {
  return request<any>(`/bi/dashboards/${dashboardId}/shares`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function removeBiDashboardShare(shareId: string) {
  return request<{ success: boolean }>(`/bi/dashboard-shares/${shareId}`, { method: 'DELETE' });
}

// BI Activity Log
export async function getBiActivityLog(params?: { entityType?: string; entityId?: string; action?: string; limit?: number; offset?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.entityType) searchParams.set('entityType', params.entityType);
  if (params?.entityId) searchParams.set('entityId', params.entityId);
  if (params?.action) searchParams.set('action', params.action);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return request<any[]>(`/bi/activity-log${query ? `?${query}` : ''}`);
}

// BI Summary Dashboard
export async function getBiSummary() {
  return request<{
    dashboards: number;
    activeMetrics: number;
    savedReports: number;
    activeAlerts: number;
    unresolvedAlerts: number;
    recentActivity: any[];
  }>('/bi/summary');
}

// ================================
// Reorder Points
// ================================

export async function getReorderPoints(params?: {
  rawMaterialId?: string;
  warehouseId?: string;
  needsReorder?: boolean;
  belowSafetyStock?: boolean;
  limit?: number;
  offset?: number;
}) {
  const query = new URLSearchParams();
  if (params?.rawMaterialId) query.set('rawMaterialId', params.rawMaterialId);
  if (params?.warehouseId) query.set('warehouseId', params.warehouseId);
  if (params?.needsReorder !== undefined) query.set('needsReorder', String(params.needsReorder));
  if (params?.belowSafetyStock !== undefined) query.set('belowSafetyStock', String(params.belowSafetyStock));
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[] }>(`/reorder-points${queryString ? `?${queryString}` : ''}`);
}

export async function getReorderPoint(id: string) {
  return request<{ data: any }>(`/reorder-points/${id}`);
}

export async function createReorderPoint(data: {
  rawMaterialId: string;
  warehouseId?: string;
  reorderPoint: string;
  reorderQty?: string;
  safetyStock?: string;
  maxStock?: string;
  unit: string;
  leadTimeDays?: number;
  averageDailyUsage?: string;
  preferredSupplierId?: string;
  autoReorderEnabled?: boolean;
}) {
  return request<{ data: any }>('/reorder-points', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateReorderPoint(id: string, data: Partial<{
  reorderPoint: string;
  reorderQty: string;
  safetyStock: string;
  maxStock: string;
  unit: string;
  leadTimeDays: number;
  averageDailyUsage: string;
  preferredSupplierId: string;
  autoReorderEnabled: boolean;
}>) {
  return request<{ data: any }>(`/reorder-points/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteReorderPoint(id: string) {
  return request<{ success: boolean }>(`/reorder-points/${id}`, { method: 'DELETE' });
}

export async function checkReorderStatus() {
  return request<{ data: { needsReorder: any[]; belowSafetyStock: any[] } }>('/reorder-points/check-status');
}
